/**
 * Shared free-text task matcher for the todo-lists arche.
 *
 * One place decides what "matching content" means so the shared List view and
 * the personal "My Tasks" board search identically. Matching covers every piece
 * of text a task carries in the loaded list data — its title, description,
 * display id (e.g. `FR-12`), work-status label, assignee name, and containing
 * section name — plus any surface-specific `extra` text (the board's "belongs
 * to X" displaced-section note). Sub-tasks are fetched lazily per row and so are
 * not part of the loaded set; they are intentionally out of scope here.
 *
 * Semantics: the query is split into whitespace-separated terms and a task
 * matches when EVERY term is a case-insensitive substring of its combined text
 * (AND across terms). An empty query matches everything.
 */

export interface TaskSearchFields {
  title?: string | null;
  description?: string | null;
  /** Human display id such as `FR-12`. */
  displayId?: string | null;
  /** Rendered work-status label, e.g. "In Progress". */
  statusLabel?: string | null;
  assigneeName?: string | null;
  /** Name of the section/column the task sits in. */
  sectionName?: string | null;
  /** Any additional searchable text specific to a surface. */
  extra?: Array<string | null | undefined>;
}

/** Split a raw query into lowercased, non-empty search terms. */
export function parseSearchTerms(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** True when every term appears somewhere in the task's combined content. */
export function taskMatchesTerms(fields: TaskSearchFields, terms: string[]): boolean {
  if (terms.length === 0) return true;
  const haystack = [
    fields.title,
    fields.description,
    fields.displayId,
    fields.statusLabel,
    fields.assigneeName,
    fields.sectionName,
    ...(fields.extra ?? []),
  ]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' ')
    .toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

/** Convenience wrapper that parses `query` and matches in one call. */
export function taskMatchesQuery(fields: TaskSearchFields, query: string): boolean {
  return taskMatchesTerms(fields, parseSearchTerms(query));
}
